import * as THREE from 'three';
import { buildCornerMarker } from './trackBuilder.js';

const START_LINE_FLAG_TEXTURE_URL = '/textures/checkered-flag.png';
const DIRECTION_TEXTURE_URL = '/direction.png';

/** Matches public/direction.png aspect (155×35). */
const DIRECTION_WIDTH = 0.3;
const DIRECTION_HEIGHT = 0.3 * (35 / 155);
const DIRECTION_SIDE_OFFSET = 0.32;
const DIRECTION_FORWARD_OFFSET = 0.14;

const POLE_HEIGHT = 0.58;
const POLE_RADIUS = 0.006;
const FLAG_SCALE = 0.75;
const FLAG_WIDTH = 0.52 * FLAG_SCALE;
const FLAG_HEIGHT = 0.31 * FLAG_SCALE;
const FLAG_SEGMENTS_X = 42;
const FLAG_SEGMENTS_Y = 26;

const FLAG_VERTEX = /* glsl */ `
uniform float uTime;
uniform vec2 uFrequency;
uniform float uStrength;
uniform float uMaxDisplacement;
uniform float uVerticalScale;
uniform float uMaxVertical;

varying float vShade;
varying vec2 vUv;

void main() {
  vec3 pos = position;

  // uv.x: 0 = hoist on pole, 1 = free edge
  float along = uv.x;
  float free = smoothstep(0.0, 0.05, along) * pow(along, 1.45);

  float t = uTime;

  float mainWave = sin(along * uFrequency.x * 3.14159 - t);
  float crossWave = sin(uv.y * uFrequency.y * 3.14159 + t * 0.6) * 0.42;
  float slowSwell = sin(along * 2.3 - t * 0.4) * 0.32;
  float ripple = sin(along * 4.5 - t * 0.95 + uv.y * 2.6) * 0.16;

  float displacement =
    (mainWave * 0.62 + crossWave + slowSwell + ripple) * free * uStrength;
  displacement = clamp(displacement, -uMaxDisplacement, uMaxDisplacement);

  pos.z += displacement;
  pos.x += displacement * 0.16 * free;

  // Vertical billow (top/bottom edge crests — header flag)
  float vertAlong1 = sin(along * uFrequency.x * 2.65 - t * 1.08);
  float vertAlong2 = sin(along * uFrequency.x * 5.0 - t * 1.42) * 0.38;
  float vertAlong3 = sin(along * 1.55 - t * 0.62) * 0.28;
  float vertCross = sin(uv.y * uFrequency.y * 3.14159 - t * 0.95) * 0.3;
  float vertDiag = sin(along * 3.2 + uv.y * 4.0 - t * 1.25) * 0.22;

  float vertical =
    (vertAlong1 + vertAlong2 + vertAlong3 + vertCross + vertDiag)
    * free
    * uStrength
    * uVerticalScale;
  vertical = clamp(vertical, -uMaxVertical, uMaxVertical);
  pos.y += vertical;

  vec4 modelPosition = modelMatrix * vec4(pos, 1.0);
  vec4 viewPosition = viewMatrix * modelPosition;
  gl_Position = projectionMatrix * viewPosition;

  vUv = uv;
  vShade = displacement + vertical * 0.5;
}
`;

const FLAG_FRAGMENT = /* glsl */ `
uniform sampler2D uTexture;
uniform float uCheckeredCutout;
uniform float uGlowBoost;
uniform float uBrightness;

varying float vShade;
varying vec2 vUv;

void main() {
  vec4 color = texture2D(uTexture, vUv);

  if (uCheckeredCutout > 0.5) {
    float lum = dot(color.rgb, vec3(0.299, 0.587, 0.114));
    if (lum < 0.52) discard;
    float lighting = 1.0 + vShade * (1.85 + uGlowBoost * 1.35);
    vec3 white = vec3(1.0) * uBrightness * clamp(lighting, 0.88, 1.0 + uGlowBoost * 0.28);
    white += vec3((0.06 + uGlowBoost * 0.14) * (1.0 + vShade * 1.5));
    gl_FragColor = vec4(white, min(1.0, 0.92 + uGlowBoost * 0.08));
  } else {
    if (color.a < 0.08) discard;
    float lighting = 1.0 + vShade * (1.25 + uGlowBoost * 1.15);
    vec3 rgb = color.rgb * uBrightness * clamp(lighting, 0.9, 1.0 + uGlowBoost * 0.32);
    rgb += vec3(uGlowBoost * 0.2 * (1.0 + vShade * 2.2));
    gl_FragColor = vec4(rgb, min(1.0, color.a * (0.96 + uGlowBoost * 0.06)));
  }
}
`;

/** @type {THREE.Texture | null} */
let startLineFlagTextureCache = null;
/** @type {Promise<THREE.Texture> | null} */
let startLineFlagTexturePromise = null;
/** @type {THREE.Texture | null} */
let directionTextureCache = null;
/** @type {Promise<THREE.Texture> | null} */
let directionTexturePromise = null;

/**
 * Checkered start-line flag only (never uses header uploads).
 * @returns {Promise<THREE.Texture>}
 */
export function loadFlagTexture() {
  if (startLineFlagTextureCache) {
    return Promise.resolve(startLineFlagTextureCache);
  }
  if (startLineFlagTexturePromise) return startLineFlagTexturePromise;

  startLineFlagTexturePromise = new Promise((resolve, reject) => {
    new THREE.TextureLoader().load(
      START_LINE_FLAG_TEXTURE_URL,
      (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.minFilter = THREE.LinearFilter;
        tex.magFilter = THREE.LinearFilter;
        startLineFlagTextureCache = tex;
        resolve(tex);
      },
      undefined,
      reject,
    );
  });

  return startLineFlagTexturePromise;
}

/**
 * @returns {Promise<THREE.Texture>}
 */
export function loadDirectionTexture() {
  if (directionTextureCache) return Promise.resolve(directionTextureCache);
  if (directionTexturePromise) return directionTexturePromise;

  directionTexturePromise = new Promise((resolve, reject) => {
    new THREE.TextureLoader().load(
      DIRECTION_TEXTURE_URL,
      (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.minFilter = THREE.LinearFilter;
        tex.magFilter = THREE.LinearFilter;
        directionTextureCache = tex;
        resolve(tex);
      },
      undefined,
      reject,
    );
  });

  return directionTexturePromise;
}

/**
 * @param {THREE.Texture} texture
 * @param {{ clockwise?: boolean }} [options]
 */
export function createDirectionArrow(texture, options = {}) {
  const { clockwise = true } = options;

  const pivot = new THREE.Group();
  pivot.position.set(DIRECTION_SIDE_OFFSET, 0.02, DIRECTION_FORWARD_OFFSET);
  pivot.rotation.y = -Math.PI / 2 + (clockwise ? 0 : Math.PI);

  const arrow = new THREE.Mesh(
    new THREE.PlaneGeometry(DIRECTION_WIDTH, DIRECTION_HEIGHT),
    new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      opacity: 0.75,
      depthWrite: false,
      toneMapped: true,
      alphaTest: 0.08,
    }),
  );

  // Lay flat on the track (XZ); pivot.rotation.y points along the lap direction.
  arrow.rotation.x = -Math.PI / 2;
  arrow.renderOrder = 6;
  pivot.add(arrow);
  pivot.userData.isDirectionArrow = true;

  return pivot;
}

/**
 * @param {THREE.Object3D} group
 * @param {boolean} clockwise
 */
export function updateStartMarkerDirection(group, clockwise) {
  const arrow = group.userData.directionArrow;
  if (!arrow) return;
  arrow.rotation.y = -Math.PI / 2 + (clockwise ? 0 : Math.PI);
}

/**
 * @param {THREE.Texture} texture
 * @param {{
 *   width?: number;
 *   height?: number;
 *   segmentsX?: number;
 *   segmentsY?: number;
 *   frequency?: { x: number; y: number };
 *   strength?: number;
 *   attachToPole?: boolean;
 *   checkeredCutout?: boolean;
 *   glowBoost?: number;
 *   brightness?: number;
 *   maxDisplacement?: number;
 *   verticalScale?: number;
 *   maxVertical?: number;
 * }} [options]
 */
export function createAnimatedFlagMesh(texture, options = {}) {
  const {
    width = FLAG_WIDTH,
    height = FLAG_HEIGHT,
    segmentsX = FLAG_SEGMENTS_X,
    segmentsY = FLAG_SEGMENTS_Y,
    frequency = { x: 3.1, y: 3.9 },
    strength = 0.068,
    attachToPole = true,
    checkeredCutout = true,
    glowBoost = 0,
    brightness = 1,
    maxDisplacement = 0.085,
    verticalScale = 0,
    maxVertical = 0.09,
  } = options;

  const flag = new THREE.Mesh(
    new THREE.PlaneGeometry(width, height, segmentsX, segmentsY),
    new THREE.ShaderMaterial({
      vertexShader: FLAG_VERTEX,
      fragmentShader: FLAG_FRAGMENT,
      uniforms: {
        uFrequency: { value: new THREE.Vector2(frequency.x, frequency.y) },
        uTime: { value: 0 },
        uTexture: { value: texture },
        uStrength: { value: strength },
        uMaxDisplacement: { value: maxDisplacement },
        uVerticalScale: { value: verticalScale },
        uMaxVertical: { value: maxVertical },
        uCheckeredCutout: { value: checkeredCutout ? 1 : 0 },
        uGlowBoost: { value: glowBoost },
        uBrightness: { value: brightness },
      },
      side: THREE.DoubleSide,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      toneMapped: true,
      alphaTest: 0.5,
    }),
  );

  flag.renderOrder = 6;

  if (attachToPole) {
    // Hoist on pole (left edge x=0); top aligned with mast
    flag.position.set(width / 2, POLE_HEIGHT - height / 2, 0.012);
  }

  flag.userData.isFlagMesh = true;
  return flag;
}

export const FLAG_ASPECT = FLAG_WIDTH / FLAG_HEIGHT;

/**
 * @param {THREE.Mesh} flagMesh
 * @param {number} elapsedSeconds
 * @param {number} [animationSpeed]
 * @param {number} [timeScale]
 */
export function updateFlagMeshAnimation(
  flagMesh,
  elapsedSeconds,
  animationSpeed = 4,
  timeScale = 1.15,
) {
  if (!flagMesh?.material?.uniforms?.uTime) return;
  flagMesh.material.uniforms.uTime.value =
    elapsedSeconds * animationSpeed * timeScale;
}

/**
 * @param {THREE.Mesh} flagMesh
 * @param {number} strength
 */
export function setFlagMeshWaveStrength(flagMesh, strength) {
  if (flagMesh?.material?.uniforms?.uStrength) {
    flagMesh.material.uniforms.uStrength.value = strength;
  }
}

function createFlagPole() {
  const pole = new THREE.Mesh(
    new THREE.CylinderGeometry(POLE_RADIUS, POLE_RADIUS * 1.1, POLE_HEIGHT, 8),
    new THREE.MeshBasicMaterial({
      color: 0x9a9a9a,
      toneMapped: true,
    }),
  );
  pole.position.set(0, POLE_HEIGHT / 2, 0);
  pole.renderOrder = 6;
  return pole;
}

const _tangent = new THREE.Vector3();
const _position = new THREE.Vector3();

/**
 * @param {THREE.Curve} curve
 * @param {number} t
 * @param {number} liftY
 * @param {THREE.Texture} flagTexture
 * @param {Parameters<typeof buildCornerMarker>[1] & { lapClockwise?: boolean }} [options]
 */
export function buildStartMarker(curve, t, liftY, flagTexture, directionTexture, options = {}) {
  const { lapClockwise = true, flagWaveStrength, ...baseOptions } = options;
  const point = curve.getPointAt(t);
  _position.copy(point);
  _position.y = liftY;

  curve.getTangentAt(t, _tangent);
  _tangent.y = 0;
  if (_tangent.lengthSq() < 1e-8) {
    _tangent.set(0, 0, 1);
  } else {
    _tangent.normalize();
  }

  const base = buildCornerMarker(_position, {
    beamHeight: 4,
    ringRadius: 0.2,
    beamRadius: 0.035,
    glowRadius: 0.12,
    color: 0xffffff,
    ringOpacity: 0.85,
    coreOpacity: 1,
    beamOpacity: 0.45,
    glowOpacity: 0.28,
    glowStrength: 1.1,
    topGlowOpacity: 0,
    showBeamGlow: true,
    ...baseOptions,
  });

  base.userData.isStartMarker = true;
  base.userData.startT = t;
  base.userData.cornerPart = undefined;

  const flagRoot = new THREE.Group();
  flagRoot.add(createFlagPole());
  flagRoot.add(
    createAnimatedFlagMesh(flagTexture, {
      strength: flagWaveStrength,
      checkeredCutout: true,
    }),
  );
  const directionArrow = createDirectionArrow(directionTexture, {
    clockwise: lapClockwise,
  });
  flagRoot.add(directionArrow);
  base.add(flagRoot);

  base.userData.flagMesh = flagRoot.children[1];
  base.userData.directionArrow = directionArrow;
  base.userData.flagRoot = flagRoot;
  base.userData.lapClockwise = lapClockwise;
  base.rotation.y = Math.atan2(_tangent.x, _tangent.z);

  return base;
}

/**
 * @param {THREE.Object3D} group
 * @param {number} elapsedSeconds
 * @param {number} [animationSpeed]
 */
export function updateStartMarkerAnimation(
  group,
  elapsedSeconds,
  animationSpeed = 4,
) {
  updateFlagMeshAnimation(group.userData.flagMesh, elapsedSeconds, animationSpeed);
}

/**
 * @param {THREE.Object3D} root
 */
export function disposeStartMarker(root) {
  root.traverse((child) => {
    if (!child.isMesh) return;
    child.geometry?.dispose();
    const mat = child.material;
    if (Array.isArray(mat)) {
      mat.forEach((m) => m.dispose());
    } else if (mat) {
      mat.dispose();
    }
  });
}
